/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useCallback, useContext, useEffect, useState} from 'react'
import {UserModel} from '../../../app/modules/auth/models/UserModel'
import {GetLoginUser} from '../../../app/shared/service/redux-store'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface SiteOption {
  id: number
  name: string
  organizationUnitName?: string
  organizationUnitLongshipId?: string
  organizationUnitParentId?: string
  organizationUnitHierarchyIds?: string[]
  organizationUnitHierarchyNames?: string[]
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
  currentUser: UserModel
  setCurrentUser: (_userModel: UserModel) => void
  siteOptions: SiteOption[]
  setSiteOptions: (_siteOptions: SiteOption[]) => void
  selectedSiteId: number | null
  setSelectedSiteId: (_siteId: number | null) => void
  selectedSiteIds: number[] | null
  setSelectedSiteIds: (_siteIds: number[] | null) => void
}

export const Initial_User: UserModel = {
  id: 0,
  username: '',
  password: '',
  email: '',
  firstName: '',
  lastName: '',
  userRoleDisplay: '',
  isResetPasswordRequired: false,
  userRoles: [],
  activePages: [],
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
  setCurrentUser: (_userModel: UserModel) => {},
  siteOptions: [],
  setSiteOptions: (_siteOptions: SiteOption[]) => {},
  selectedSiteId: null,
  setSelectedSiteId: (_siteId: number | null) => {},
  selectedSiteIds: null,
  setSelectedSiteIds: (_siteIds: number[] | null) => {},
  currentUser: Initial_User,
})

const SELECTED_SITE_STORAGE_KEY = 'selectedSiteId'
const SELECTED_SITE_IDS_STORAGE_KEY = 'selectedSiteIds'

const normalizeSiteIds = (siteIds: number[] | null | undefined) => {
  if (!Array.isArray(siteIds)) {
    return null
  }

  const normalized = Array.from(
    new Set(
      siteIds
        .map((siteId) => Number(siteId))
        .filter((siteId) => Number.isFinite(siteId))
    )
  )

  return normalized.length > 0 ? normalized : null
}

const parseStoredSiteId = () => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null
  }

  const storedValue = localStorage.getItem(SELECTED_SITE_STORAGE_KEY)
  if (!storedValue) {
    return null
  }

  const parsed = Number(storedValue)
  return Number.isFinite(parsed) ? parsed : null
}

const parseStoredSiteIds = () => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null
  }

  const storedValue = localStorage.getItem(SELECTED_SITE_IDS_STORAGE_KEY)
  if (!storedValue) {
    return null
  }

  try {
    const parsed = JSON.parse(storedValue)
    return normalizeSiteIds(Array.isArray(parsed) ? parsed : null)
  } catch {
    return null
  }
}

const PageDataProvider: React.FC = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])
  const [currentUser, setCurrentUser] = useState<UserModel>(GetLoginUser())
  const [siteOptions, setSiteOptions] = useState<SiteOption[]>([])
  const [selectedSiteId, setSelectedSiteIdState] = useState<number | null>(() => parseStoredSiteId())
  const [selectedSiteIds, setSelectedSiteIdsState] = useState<number[] | null>(() => {
    const storedSiteIds = parseStoredSiteIds()
    if (storedSiteIds) {
      return storedSiteIds
    }

    const storedSiteId = parseStoredSiteId()
    return storedSiteId === null ? null : [storedSiteId]
  })

  const setSelectedSiteId = useCallback((siteId: number | null) => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      setSelectedSiteIdState(siteId)
      setSelectedSiteIdsState(siteId === null ? null : [siteId])
      return
    }

    setSelectedSiteIdState(siteId)
    setSelectedSiteIdsState(siteId === null ? null : [siteId])
    if (siteId === null) {
      localStorage.removeItem(SELECTED_SITE_STORAGE_KEY)
      localStorage.removeItem(SELECTED_SITE_IDS_STORAGE_KEY)
      return
    }

    localStorage.setItem(SELECTED_SITE_STORAGE_KEY, siteId.toString())
    localStorage.setItem(SELECTED_SITE_IDS_STORAGE_KEY, JSON.stringify([siteId]))
  }, [])

  const setSelectedSiteIds = useCallback((siteIds: number[] | null) => {
    const normalizedSiteIds = normalizeSiteIds(siteIds)
    const hasSingleSite = normalizedSiteIds !== null && normalizedSiteIds.length === 1
    const singleSiteId = hasSingleSite ? normalizedSiteIds[0] : null

    setSelectedSiteIdsState(normalizedSiteIds)
    setSelectedSiteIdState(singleSiteId)

    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return
    }

    if (normalizedSiteIds === null) {
      localStorage.removeItem(SELECTED_SITE_IDS_STORAGE_KEY)
      localStorage.removeItem(SELECTED_SITE_STORAGE_KEY)
      return
    }

    localStorage.setItem(SELECTED_SITE_IDS_STORAGE_KEY, JSON.stringify(normalizedSiteIds))
    if (singleSiteId === null) {
      localStorage.removeItem(SELECTED_SITE_STORAGE_KEY)
      return
    }

    localStorage.setItem(SELECTED_SITE_STORAGE_KEY, singleSiteId.toString())
  }, [])

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    currentUser,
    setCurrentUser,
    siteOptions,
    setSiteOptions,
    selectedSiteId,
    setSelectedSiteId,
    selectedSiteIds,
    setSelectedSiteIds,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
}

const PageTitle: FC<Props> = ({children, description, breadcrumbs}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  return <></>
}

const PageDescription: React.FC = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, PageTitle, PageDataProvider, usePageData}
